import { IsTablet } from '@24i/nxg-core-utils/src/globalSingletons';
import DeviceInfo from '@24i/nxg-sdk-quantum/src/DeviceInfo';
import { isFactorMobile, isPlatformWeb } from 'renative';

/**
 * Utility function to determine if phone behavior should be used for image selection.
 * This is shared logic between the original client and the context wrapper.
 *
 * According to the acceptance criteria:
 * Mobile web with portrait orientation should use portrait images.
 * Mobile web with landscape orientation should use landscape images (like desktop/tablet).
 * Tablet web should always use landscape images.
 * Native mobile (phone) should use portrait images.
 * Native mobile (tablet) should use landscape images.
 *
 * @param screenWidth - Current screen width
 * @param considerTablet - Whether to consider tablets as non-phones (defaults to true)
 * @returns True if phone-optimized images should be used
 */
export const isPhoneForImageSelection = (screenWidth: number, considerTablet = true): boolean => {
    // Check if we're on a tablet
    const isTablet = IsTablet.get();

    // When considerTablet is false, always use phone behavior (return true)
    // This is important for all test cases where considerTablet=false
    if (!considerTablet) {
        return true;
    }

    // For native devices:
    if (isFactorMobile) {
        // For native mobile devices:
        // - Phone behavior for phones (true)
        // - Not phone behavior for tablets (false)
        return !isTablet;
    }

    // For web platform:
    if (isPlatformWeb) {
        if (isTablet) {
            // Tablet web always uses tablet behavior (false)
            return false;
        }

        // For mobile web (non-tablet), check orientation
        const isLandscape = DeviceInfo.isLandscapeSync();

        // Mobile web in portrait: use phone behavior (true)
        // Mobile web in landscape: use tablet behavior (false)
        return !isLandscape;
    }

    // Default case: not mobile and not web
    return false;
};
